import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Bot, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Where should I buy groceries this week?",
  "Find me a family vacation under $3,000",
  "What credit card gives the best cashback?",
  "Which subscriptions should I cancel?",
  "Can I save more on my phone bill?",
  "Find a better price for paper towels",
];

export default function Ria() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm Ria 👋 — your AI savings co-pilot. Ask me about groceries, travel, credit cards, subscriptions, or anything you're shopping for." },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim()) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: msg });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error("Ria is busy. Try again in a moment.");
      setMessages((m) => [...m, { role: "assistant", content: "I'm having trouble reaching my AI brain right now. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-ria">
      <PageHeader
        eyebrow="AI Assistant"
        title="Ria · Your savings co-pilot"
        subtitle="Ask anything about deals, recommendations, and smarter spending."
      />

      <div className="grid lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-3 border-gray-200 shadow-none rounded-2xl flex flex-col h-[70vh]">
          <CardContent className="flex-1 overflow-hidden p-0 flex flex-col">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${i}`}>
                  {m.role !== "user" && (
                    <div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-700 border border-sky-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4" />
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-4 py-3 max-w-[80%] text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-emerald-700 text-white"
                        : "bg-gray-50 text-gray-800 border border-gray-200"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex gap-3" data-testid="ria-thinking">
                  <div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-700 border border-sky-100 flex items-center justify-center"><Bot className="w-4 h-4" /></div>
                  <div className="rounded-2xl bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-500">Ria is thinking…</div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-gray-200 p-4 flex gap-2"
              data-testid="ria-form"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Ria a question…"
                className="flex-1"
                disabled={sending}
                data-testid="ria-input"
              />
              <Button type="submit" disabled={sending} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl" data-testid="ria-send">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="border-sky-100 shadow-none rounded-2xl gradient-sky">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-sky-700" />
                <div className="text-xs uppercase tracking-[0.18em] text-sky-700 font-semibold">Try asking</div>
              </div>
              <div className="space-y-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    data-testid={`suggestion-${i}`}
                    className="text-left text-sm text-gray-800 hover:text-emerald-800 w-full px-3 py-2 rounded-lg hover:bg-white/60 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 shadow-none rounded-2xl">
            <CardContent className="p-5 text-xs text-gray-500 leading-relaxed">
              Ria gives general guidance. Cashback is subject to merchant confirmation. Credit card recommendations are not financial advice.
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
